import React, { useState, useEffect } from "react";
import { Container, Row, Col, Tab, Nav, Form, Button, Carousel } from "react-bootstrap";
import ReactSlider from "react-slider";
import { useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import axios from "axios";
import { API_BASE_URL } from '../config/api';
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";
import { useLanguage } from "../contexts/LanguageContext";

const ToolsDownload = () => {
	const L = useLanguage();
	const [username, setUsername] = useState("");
	const [generating, setGenerating] = useState(false);
	const [posts, setPosts] = useState([]);
	const [videos, setVideos] = useState([]);
	const [foundPost, setFoundPost] = useState(true);
	const [pagination, setPagination] = useState();
	const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

	const downloadPost = async () => {
		try {
			// Resetting states for initial request, not clearing them for paginated requests
			if (pagination === null) {
				setPosts([]);
				setVideos([]);
				setFoundPost(true);
			}
			setGenerating(true);

			const formData = {
				username: username,
				pagination_token: pagination,
			};

			const response = await axiosClient.post("https://api.viralgpt.com/tools/download", formData, {
				headers: {
					"Content-Type": "application/json",
				},
			});

			setGenerating(false);

			// Append the new data to existing arrays
			setPosts((prevPosts) => [...prevPosts, ...response.data.posts]);
			setVideos((prevVideos) => [...prevVideos, ...response.data.videos]);

			if (response.data.posts.length === 0 && response.data.videos.length === 0) {
				setFoundPost(false);
			} else {
				setFoundPost(true);
			}

			if (response.data.pagination_token) {
				setPagination(response.data.pagination_token);
			} else {
				setPagination(null);
			}

			console.log("Server response:", response.data);
			setFunctionSuccess(L.toolsDownload.success);
		} catch (e) {
			setGenerating(false);
			setFunctionError(L.toolsDownload.downloadError(e?.response?.data?.error));
		}
	};

	// Helper to deeply flatten carousels/posts for rendering in grid if needed (not used in this version, but can be helpful)
	const flattenDeep = (arr) =>
		Array.isArray(arr)
			? arr.reduce((a, v) => a.concat(flattenDeep(v)), [])
			: [arr];

	// Processes posts array to extract image groups
	// Each post can be:
	// - A string (single image)
	// - An array of strings (carousel)
	// - Nested arrays that need to be unwrapped
	const getImageGroups = (postsArr) => {
		const result = [];

		for (const post of postsArr) {
			if (typeof post === "string") {
				// Single image
				result.push([post]);
			} else if (Array.isArray(post)) {
				// Could be a carousel or nested array
				// Unwrap nested arrays until we get to the actual images
				let unwrapped = post;
				while (
					Array.isArray(unwrapped) &&
					unwrapped.length === 1 &&
					Array.isArray(unwrapped[0])
				) {
					unwrapped = unwrapped[0];
				}

				// Now check if it's an array of strings (carousel)
				if (unwrapped.length > 0 && typeof unwrapped[0] === "string") {
					result.push(unwrapped);
				} else if (Array.isArray(unwrapped)) {
					// Further nested - flatten one more level
					for (const item of unwrapped) {
						if (typeof item === "string") {
							result.push([item]);
						} else if (Array.isArray(item)) {
							result.push(item);
						}
					}
				}
			}
		}

		console.log("Processed image groups:", result);
		return result;
	};

	return (
		<>
			<div className="bg-white border  p-6">
				<h5>{L.toolsDownload.heading}</h5>
				<p className="text-muted">{L.toolsDownload.subtitle}</p>

				<Alert
					variant="danger"
					message={functionError}
					show={!!functionError}
					onClose={() => setFunctionError("")}
				/>

				<Alert
					variant="success"
					message={functionSuccess}
					show={!!functionSuccess}
					onClose={() => setFunctionSuccess("")}
				/>

				{/* Link Input */}
				<Form.Group controlId="commentsLink" className="mb-3">
					<Form.Label>{L.toolsDownload.inputLabel}</Form.Label>
					<Form.Control
						type="text"
						placeholder={L.toolsDownload.inputPlaceholder}
						value={username}
						onChange={(e) => setUsername(e.target.value)}
					/>
				</Form.Group>

				<div className="d-flex align-items-center">
					<Button className={generating || username == '' ? "disabled bg-instagram" : "bg-instagram"} onClick={() => downloadPost()}>
						{username == '' && (L.toolsDownload.buttonEmpty)}
						{username.includes(":") && (L.toolsDownload.buttonDownloadPost)}
						{!username.includes(":") && username.length > 0 && (L.toolsDownload.buttonSearchProfile)}
					</Button>
					<div style={{ fontSize: "1.5rem" }} className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!generating ? "d-none" : ""}`} />
				</div>
				{foundPost == false ? <p>{L.toolsDownload.noPosts}</p> : <p></p>}

				{(posts.length > 0 || videos.length > 0) && (
					<Tab.Container defaultActiveKey={posts.length > 0 ? "posts" : "reels"}>
						<Nav variant="tabs" className="mb-4">
							{posts.length > 0 && (
								<Nav.Item>
									<Nav.Link eventKey="posts"><i className="fa fa-table-cells me-2"></i>{L.toolsDownload.tabPosts}</Nav.Link>
								</Nav.Item>
							)}
							{videos.length > 0 && (
								<Nav.Item>
									<Nav.Link eventKey="reels"><i className="fa fa-clapperboard me-2"></i>{L.toolsDownload.tabReels}</Nav.Link>
								</Nav.Item>
							)}
						</Nav>

						<Tab.Content>

							{/* Posts Tab */}
							<Tab.Pane eventKey="posts">
								<div className="row">
									{getImageGroups(posts).map((group, groupIdx) => (

										<div
											key={groupIdx}
											className="col-12 col-md-6 col-lg-4 position-relative"
											style={{
												marginBottom: "1rem",
											}}
										>
											{group.length === 1 ? (
												<>
													<img src={group[0]} alt="media" className="h-auto" style={{ width: "100%" }} />
													<Button
														className="bg-instagram mt-2 position-absolute top-0 end-0 m-3"
														onClick={() => window.open(group[0], "_blank")}
													>
														<i className="fa fa-download"></i>
													</Button>
												</>
											) : (
												<div className="carousel-group">
													<Carousel interval={null} indicators={true} controls={true}>
														{group.map((imgUrl, idx) => (
															<Carousel.Item key={idx}>
																<img
																	src={imgUrl}
																	alt={`carousel-media-${groupIdx}-${idx}`}
																	className="d-block w-100 h-auto"
																/>
																<Button
																	className="bg-instagram position-absolute top-0 end-0 m-3"
																	onClick={() => window.open(imgUrl, "_blank")}
																	style={{ zIndex: 5 }}
																>
																	<i className="fa fa-download"></i> {idx + 1}/{group.length}
																</Button>
															</Carousel.Item>
														))}
													</Carousel>
												</div>
											)}
										</div>
									))}
								</div>
							</Tab.Pane>

							{/* Reels Tab */}
							<Tab.Pane eventKey="reels">
								<div className="row">
									{videos.map((idea, index) => (
										<div
											key={index}
											className="col-12 col-md-6 col-lg-4 position-relative"
											style={{
												marginBottom: "1rem",
											}}
										>
											<video className="h-auto" controls>
												<source src={idea} type="video/mp4" />
												{L.toolsDownload.videoUnsupported}
											</video>
											<Button
												variant="primary"
												className="bg-instagram mt-2 position-absolute top-0 end-0 m-3"
												onClick={() => window.open(idea, "_blank")}
											>
												<i className="fa fa-download"></i>
											</Button>
										</div>
									))}
								</div>
							</Tab.Pane>

						</Tab.Content>
						<div className="d-flex justify-content-center mt-4">
							<Button
								className={generating ? "disabled bg-instagram w-100" : "bg-instagram w-100"}
								onClick={downloadPost}
								disabled={generating || !pagination}
							>
								{generating ? L.toolsDownload.loading : pagination ? L.toolsDownload.loadMore : L.toolsDownload.noMoreData}
							</Button>
						</div>
					</Tab.Container>
				)}
			</div>
		</>
	);
};

export default ToolsDownload;
